import { useTranslation } from 'react-i18next';
import type { Question } from '../../types';
import { PREVIEW_PARTICIPANTS, PREVIEW_EMOJI } from '../../data';
import { EmojiFace, NoObsFace } from '../EmojiFace';

interface PreviewCollectiveOptionsProps {
  q: Question;
}

export function PreviewCollectiveOptions({ q }: PreviewCollectiveOptionsProps) {
  const { t } = useTranslation();
  const opts = q.options.length > 0 ? q.options : [];
  const isColectivo = q.enfoque === 'colectivo';

  // behaviors + matrix → render like multiple (option columns, no emoji faces)
  if (q.type === 'multiple' || q.type === 'behaviors') {
    const cols = opts.length > 0 ? opts : [t('admin.builderPreviewOption1'), t('admin.builderPreviewOption2')];

    // colectivo: no person rows — show a standalone option list
    if (isColectivo) {
      return (
        <div className="pvx-ind-mc">
          {cols.map((opt, oi) => (
            <div key={oi} className="pvx-ind-mc-opt">
              <span className="pvx-dot" />
              <span style={{ fontSize: '0.8rem' }}>{opt}</span>
            </div>
          ))}
          <div className="pvx-ind-mc-opt" style={{ opacity: 0.45, fontStyle: 'italic', fontSize: '0.8rem' }}>
            <span className="pvx-dot" /> {t('admin.builderPreviewNoObs')}
          </div>
        </div>
      );
    }

    // individual + matrix: grid with person rows and option columns
    return (
      <div className="pvx-matrix" style={{ overflowX: 'auto' }}>
        <div className="pvx-row pvx-row-head">
          <div className="pvx-name-cell" />
          {cols.map((opt, oi) => (
            <div key={oi} className="pvx-col-head">
              <span className="pvx-mc-label">{opt}</span>
            </div>
          ))}
          <div className="pvx-col-head">
            <span className="pvx-mc-label" style={{ opacity: 0.45, fontStyle: 'italic' }}>
              {t('admin.builderPreviewNoObs')}
            </span>
          </div>
        </div>
        {PREVIEW_PARTICIPANTS.map(p => (
          <div key={p.initials} className="pvx-row">
            <div className="pvx-name-cell">
              <span className="pvx-avatar">{p.initials}</span>
              <span className="pvx-name">{p.name}</span>
            </div>
            {cols.map((_, oi) => (
              <div key={oi} className="pvx-cell"><span className="pvx-dot" /></div>
            ))}
            <div className="pvx-cell"><span className="pvx-dot" style={{ background: '#e5e7eb' }} /></div>
          </div>
        ))}
      </div>
    );
  }

  if (q.type === 'nps') return (
    <div className="pvx-rating-row">
      {Array.from({ length: 10 }, (_, i) => <div key={i} className="pvx-rating-opt">{i + 1}</div>)}
    </div>
  );

  if (q.type === 'open') {
    // colectivo: no person rows — just a text area placeholder
    if (isColectivo) {
      return (
        <div style={{ background: 'white', borderRadius: 8, border: '1.5px solid rgba(0,0,0,0.08)', padding: '10px 12px', fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
          {t('admin.builderPreviewWriteAnswer')}
        </div>
      );
    }
    return (
      <div className="pvx-matrix-table">
        {PREVIEW_PARTICIPANTS.map(p => (
          <div key={p.initials} className="pvx-matrix-row pvx-matrix-row-text">
            <span className="pvx-matrix-person">
              <span className="pvx-avatar">{p.initials}</span>
              <span className="pvx-name">{p.name}</span>
            </span>
            <span className="pvx-answer-placeholder" />
          </div>
        ))}
      </div>
    );
  }

  // rating type
  if (isColectivo) {
    // colectivo: standalone emoji scale without person rows
    return (
      <div className="pvx-ind-opts">
        {PREVIEW_EMOJI.map(e => (
          <div key={e.label} className="pvx-ind-opt">
            <EmojiFace {...e} />
            <span>{e.label}</span>
          </div>
        ))}
        <div className="pvx-ind-opt"><NoObsFace /><span>{t('admin.builderPreviewNA')}</span></div>
      </div>
    );
  }

  // individual + matrix + rating: emoji faces as column headers, person rows
  return (
    <div className="pvx-matrix-table">
      <div className="pvx-matrix-head">
        <span />
        {PREVIEW_EMOJI.map(e => (
          <span key={e.label} className="pvx-matrix-col"><EmojiFace {...e} /></span>
        ))}
        <span className="pvx-matrix-col"><NoObsFace /></span>
      </div>
      {PREVIEW_PARTICIPANTS.map(p => (
        <div key={p.initials} className="pvx-matrix-row">
          <span className="pvx-matrix-person">
            <span className="pvx-avatar">{p.initials}</span>
            <span className="pvx-name">{p.name}</span>
          </span>
          {PREVIEW_EMOJI.map(e => (
            <span key={e.label} className="pvx-matrix-cell pvx-radio" style={{ borderColor: `${e.color}33` }} />
          ))}
          <span className="pvx-matrix-cell pvx-radio pvx-no-obs" />
        </div>
      ))}
    </div>
  );
}
